/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `segment_file` message.
 *
 * Summary of the unique segment and leaderboard information associated with a segment file. This message is used to compile a segment list file describing all segment files on a device. The segment list file is used when refreshing the contents of a segment file with the latest available leaderboard information.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SegmentFileMesg : Mesg {
    public constructor() : super("SegmentFile", Profile.MesgNum.SEGMENT_FILE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** file_uuid. UUID of the segment file */
    public var fileUuid: String?
        get() = getFieldValue(FILE_UUID_FIELD_NUM) as? String
        set(value) = setFieldValue(FILE_UUID_FIELD_NUM, value)

    /** enabled. Enabled state of the segment file */
    public var enabled: Boolean?
        get() = (getFieldValue(ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** user_profile_primary_key. Primary key of the user that created the segment file */
    public var userProfilePrimaryKey: UInt?
        get() = getFieldValue(USER_PROFILE_PRIMARY_KEY_FIELD_NUM) as? UInt
        set(value) = setFieldValue(USER_PROFILE_PRIMARY_KEY_FIELD_NUM, value)

    /** Number of values stored in leader_type. */
    public val numLeaderType: Int
        get() = getField(LEADER_TYPE_FIELD_NUM)?.numValues ?: 0

    /** leader_type. Leader type of each leader in the segment file */
    public var leaderType: List<SegmentLeaderboardType?>?
        get() {
            val field = getField(LEADER_TYPE_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeaderType(it) }
        }
        set(value) {
            removeField(LEADER_TYPE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeaderType(index, element) }
        }

    public fun getLeaderType(index: Int): SegmentLeaderboardType? =
        (getFieldValue(LEADER_TYPE_FIELD_NUM, index) as? UByte)?.let { SegmentLeaderboardType.fromValue(it) }

    public fun setLeaderType(index: Int, value: SegmentLeaderboardType?) {
        setFieldValue(LEADER_TYPE_FIELD_NUM, value?.value, index)
    }

    /** Number of values stored in leader_group_primary_key. */
    public val numLeaderGroupPrimaryKey: Int
        get() = getField(LEADER_GROUP_PRIMARY_KEY_FIELD_NUM)?.numValues ?: 0

    /** leader_group_primary_key. Group primary key of each leader in the segment file */
    public var leaderGroupPrimaryKey: List<UInt?>?
        get() {
            val field = getField(LEADER_GROUP_PRIMARY_KEY_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeaderGroupPrimaryKey(it) }
        }
        set(value) {
            removeField(LEADER_GROUP_PRIMARY_KEY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeaderGroupPrimaryKey(index, element) }
        }

    public fun getLeaderGroupPrimaryKey(index: Int): UInt? =
        getFieldValue(LEADER_GROUP_PRIMARY_KEY_FIELD_NUM, index) as? UInt

    public fun setLeaderGroupPrimaryKey(index: Int, value: UInt?) {
        setFieldValue(LEADER_GROUP_PRIMARY_KEY_FIELD_NUM, value, index)
    }

    /** Number of values stored in leader_activity_id. */
    public val numLeaderActivityId: Int
        get() = getField(LEADER_ACTIVITY_ID_FIELD_NUM)?.numValues ?: 0

    /** leader_activity_id. Activity ID of each leader in the segment file */
    public var leaderActivityId: List<UInt?>?
        get() {
            val field = getField(LEADER_ACTIVITY_ID_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeaderActivityId(it) }
        }
        set(value) {
            removeField(LEADER_ACTIVITY_ID_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeaderActivityId(index, element) }
        }

    public fun getLeaderActivityId(index: Int): UInt? =
        getFieldValue(LEADER_ACTIVITY_ID_FIELD_NUM, index) as? UInt

    public fun setLeaderActivityId(index: Int, value: UInt?) {
        setFieldValue(LEADER_ACTIVITY_ID_FIELD_NUM, value, index)
    }

    /** Number of values stored in leader_activity_id_string. */
    public val numLeaderActivityIdString: Int
        get() = getField(LEADER_ACTIVITY_ID_STRING_FIELD_NUM)?.numValues ?: 0

    /** leader_activity_id_string. String version of the activity ID of each leader in the segment file. 21 characters long for each ID, express in decimal */
    public var leaderActivityIdString: List<String?>?
        get() {
            val field = getField(LEADER_ACTIVITY_ID_STRING_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeaderActivityIdString(it) }
        }
        set(value) {
            removeField(LEADER_ACTIVITY_ID_STRING_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeaderActivityIdString(index, element) }
        }

    public fun getLeaderActivityIdString(index: Int): String? =
        getFieldValue(LEADER_ACTIVITY_ID_STRING_FIELD_NUM, index) as? String

    public fun setLeaderActivityIdString(index: Int, value: String?) {
        setFieldValue(LEADER_ACTIVITY_ID_STRING_FIELD_NUM, value, index)
    }

    /** default_race_leader. Index for the Leader Board entry selected as the default race participant */
    public var defaultRaceLeader: UByte?
        get() = getFieldValue(DEFAULT_RACE_LEADER_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEFAULT_RACE_LEADER_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val FILE_UUID_FIELD_NUM: UByte = 1u
        public const val ENABLED_FIELD_NUM: UByte = 3u
        public const val USER_PROFILE_PRIMARY_KEY_FIELD_NUM: UByte = 4u
        public const val LEADER_TYPE_FIELD_NUM: UByte = 7u
        public const val LEADER_GROUP_PRIMARY_KEY_FIELD_NUM: UByte = 8u
        public const val LEADER_ACTIVITY_ID_FIELD_NUM: UByte = 9u
        public const val LEADER_ACTIVITY_ID_STRING_FIELD_NUM: UByte = 10u
        public const val DEFAULT_RACE_LEADER_FIELD_NUM: UByte = 11u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[1] = Field("FileUuid", 1u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[3] = Field("Enabled", 3u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[4] = Field("UserProfilePrimaryKey", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[7] = Field("LeaderType", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("LeaderGroupPrimaryKey", 8u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[9] = Field("LeaderActivityId", 9u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[10] = Field("LeaderActivityIdString", 10u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[11] = Field("DefaultRaceLeader", 11u, BaseType.UINT8, 1.0, 0.0, "", false)
            return fields
        }
    }
}
